export type TrackingSectionContext =
  | TrackingSimpleSectionContext
  | TrackingStreamSectionContext
  | TrackingRecoSectionContext;

interface CommonTrackingSectionContext {
  readonly sectionIndexOnPage: number;
  readonly linkedContent?: string;
  readonly linkedContentId?: number;
}

/**
 * Section context used for primary / non-reco sections on a page.
 *
 * This includes, but is not limited to, basic building blocks like:
 * - Gallery and buy box on ADP
 * - Product rows on category page, wishlist, or basket
 */
export interface TrackingSimpleSectionContext
  extends CommonTrackingSectionContext {
  readonly type: 'simple';

  readonly blockTypeAndId?: `${string}_${string}`;
}

/**
 * Section context used for primary / non-reco sections on a page.
 *
 * This includes, but is not limited to, basic building blocks like:
 * - Gallery and buy box on ADP
 * - Product rows on category page, wishlist, or basket
 */
export interface TrackingStreamSectionContext
  extends CommonTrackingSectionContext {
  readonly type: 'stream';

  readonly sortChannel: string;
  readonly recoType?: string;
}

/**
 * Section context used for recommendations.
 *
 * Since `elements` contains a list of all reco childrens,
 * this requires that those be known at tracking time.
 */
export interface TrackingRecoSectionContext
  extends CommonTrackingSectionContext {
  readonly type: 'reco';

  readonly recoType: RecoSectionContent | BrowseLaneTypes;
  readonly elements: ReadonlyArray<TrackingRecoElement>;

  /// The amount of columns that are visible without scrolling
  ///
  /// e.g. 2.5
  readonly initiallyVisibleColumns: number;

  /**
   * Headline of the reco
   *
   * This means the visually shown text to the user.
   *
   * E.g. "Produkte von deiner Wunschliste" (from empty basket),
   * or "Outfits of the Week" (outfit recos on browse)
   */
  readonly headline?: string;
}

export type TrackingRecoElement = {
  // e.g. product id, ...
  readonly id: number;

  readonly type:
    | 'product'
    | 'category'
    | 'story'
    | 'outfit'
    | 'brand'
    | 'idol'
    | 'teaser';
};

export enum RecoSectionContent {
  crossSellingReco = 'cross_selling_reco',
  lastSeenProducts = 'lastSeenProducts',
  wishlistProducts = 'wishlistProducts',
  storyRecommendation = 'storyRecommendation',
  storyRecommendationCategory = 'storyCategoryReco',
  premiumStories = 'premiumStoriesReco',
  storyProducts = 'storyWithProductsReco',
  outfitRecommendationProducts = 'outfitWithProductsReco',
  outfitRecommendationCategory = 'outfitCategoryReco',
  premiumOutfits = 'premiumOutfitsReco',
  brandRecommendationWithProducts = 'brandWithProductsReco',
  shopTheLookReco = 'shopTheLookReco',
  similarProducts = 'similarProductsReco',
  fittingProducts = 'fittingProductsReco',
  recentlyViewedProductsReco = 'recentlyViewedProductsReco',
  brandLogo = 'brandLogo',
  newPremiumProducts = 'newPremiumProductsReco',
  basOSPSubscription = 'basOSPSubscription',
  outfitRecoSlider = 'outfitRecoSlider',
  trendingStories = 'trendingStories',
  topBrands = 'topBrands',
  producRecommendation = 'producRecommendation',
  storyOutfits = 'storyOutfits',
  trendingOutfits = 'trendingOutfits',
  basketFlyoutFittingProductsReco = 'basketFlyoutFittingProductsReco',
  basketFlyoutSimilarProductsReco = 'basketFlyoutSimilarProductsReco',
  customerBrand = 'customerBrand',
  staticCategories = 'staticCategories',
  featuredStoryClusters = 'featuredStoryClusters',
  outfit_adp_reco = 'outfit_adp_reco',
  shop_the_look_reco = 'shop_the_look_reco',
  auto_generated_outfits = 'auto_generated_outfits',
}

type gender = 'male' | 'female' | 'kids';

export type BrowseLaneTypes =
  | 'outfit_trending_overall'
  | 'outfit_trending_brand_performance_specific'
  | 'outfit_trending_brand_performance_general'
  | 'outfit_trending_brand_trending_specific'
  | 'outfit_trending_brand_trending_general'
  | 'outfit_trending_category_performance_specific'
  | 'outfit_trending_category_performance_general'
  | 'outfit_trending_category_trending_specific'
  | 'outfit_trending_category_trending_general'
  | 'outfit_trending_idol_specific'
  | 'outfit_trending_idol_general'
  | 'outfit_new_overall'
  | 'outfit_new_personalized_idol_specific'
  | 'outfit_new_personalized_idol_general'
  | 'outfit_trending_personalized_product_general'
  | 'outfit_trending_personalized_brand_specific'
  | 'outfit_trending_personalized_brand_general'
  | 'outfit_trending_personalized_idol_specific'
  | 'outfit_trending_personalized_idol_general'
  | 'outfit_trending_retarget_product_general'
  | 'outfit_trending_retarget_brand_general'
  | 'outfit_trending_retarget_category_specific'
  | 'outfit_trending_retarget_category_general'
  | 'story_trending_overall'
  | 'story_trending_brand_performance_specific'
  | 'story_trending_brand_performance_general'
  | 'story_trending_brand_trending_specific'
  | 'story_trending_brand_trending_general'
  | 'story_trending_category_performance_specific'
  | 'story_trending_category_performance_general'
  | 'story_trending_category_trending_specific'
  | 'story_trending_category_trending_general'
  | 'story_trending_idol_specific'
  | 'story_trending_idol_general'
  | 'story_trending_personalized_cluster_specific'
  | 'story_trending_personalized_cluster_general'
  | 'story_trending_personalized_product_general'
  | 'story_trending_personalized_brand_specific'
  | 'story_trending_personalized_brand_general'
  | 'story_trending_personalized_idol_specific'
  | 'story_trending_personalized_idol_general'
  | 'story_trending_retarget_product'
  | 'story_trending_retarget_brand_specific'
  | 'story_trending_retarget_brand_general'
  | 'story_trending_retarget_category_specific'
  | 'story_trending_retarget_category_general'
  | 'story_list_title'
  | 'product_sold_out_soon'
  | 'product_on_sale'
  | 'product_top_category_specific'
  | 'product_trending_category_specific'
  | 'product_top_personalized_category_specific'
  | 'product_trending_personalized_category_specific'
  | 'product_new_category_specific'
  | 'product_new_category_personalized_specific'
  | 'product_new_brand_personalized_specific'
  | 'product_top_category_retarget'
  | 'product_top_brand_retarget'
  | 'product_brand_personalized_specific'
  | 'brand_top_overall'
  | 'brand_trending_overall'
  | 'brand_top_personalized'
  | 'brand_top_personalized_similar'
  | 'story_bas_trending_brand_general'
  | 'local_lane'
  | 'outfit_bas_trending_brand_specific'
  | 'outfit_bas_trending_brand_general'
  | 'story_main_idol_general'
  | `featured_story_cluster_${gender}_${number}`;
